import { Component, input, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { InventoryService, InventoryType } from '@servicios/inventory/inventory.service';
import { Column } from '@sharedComponents/table-dynamic/interfaces';
import { MessageService, ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-table-stock-movements',
  templateUrl: './table-stock-movements.component.html',
  styleUrls: ['./table-stock-movements.component.css'],
  standalone: false
})
export class TableStockMovementsComponent {
  private service = inject(InventoryService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  type: InventoryType = 'stock-movements';

  loading = signal<boolean>(false);
  data = signal<any[]>([]);
  totalRecords = signal<number>(0);
  first = signal<number>(0);
  pageSize = signal<number>(10);
  globalFilter = signal<string>('');
  cols = signal<Column[]>([]);

  refreshTrigger = input<number>(0);
  branchId = input<string | null>(null);

  // Ordenamiento
  sortField = signal<string>('createdAt');
  sortOrder = signal<number>(-1);

  movementTypes = [
    { label: 'Entrada', value: 'IN' },
    { label: 'Salida', value: 'OUT' },
    { label: 'Ajuste', value: 'ADJUSTMENT' },
    { label: 'Transferencia', value: 'TRANSFER' },
    { label: 'Bloqueo / Cuarentena', value: 'BLOCK' },
    { label: 'Desbloqueo', value: 'UNBLOCK' }
  ];

  actionButtons = signal<any[]>([
    { label: 'Editar', icon: 'pi pi-pencil', type: 'edit' },
    { label: 'Eliminar', icon: 'pi pi-trash', type: 'delete' }
  ]);

  constructor() {
    effect(() => {
      const trigger = this.refreshTrigger();
      const branch = this.branchId();
      untracked(() => {
        this.loadConfig();
        this.loadData().catch(console.error);
      });
    });
  }

  loadConfig() {
    let columns: Column[] = [
      { field: 'id', header: 'ID', visible: false },
      { field: 'movement_code_display', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
      { 
        field: 'type', 
        header: 'Tipo', 
        visible: true, 
        pipe: 'movement_type',
        filter: { 
            enable: true, 
            type: 'multiselect', 
            matchMode: 'in', 
            showMenu: true,
            options: this.movementTypes.map(t => ({ name: t.label, value: t.value }))
        } 
      },
      { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
      { 
        field: 'createdAt', 
        header: 'Fecha', 
        visible: true, 
        pipe: 'date',
        filter: {
            enable: true,
            type: 'date-range',
            matchMode: 'between',
            showMenu: true
        }
      }
    ];

    this.cols.set(columns);
  }

  async loadData(event?: any) {
    try {
      this.loading.set(true);
      if (event?.first !== undefined) {
        this.first.set(event.first);
      }
      if (event?.sortField) {
        this.sortField.set(event.sortField);
      }
      if (event?.sortOrder !== undefined) {
        this.sortOrder.set(event.sortOrder);
      }
      if (event?.rows !== undefined) {
          this.pageSize.set(event.rows);
      }
      if (event?.globalFilter !== undefined) {
        this.globalFilter.set(event.globalFilter);
      }

      const res = await this.service.getAll(this.type, {
        page: (this.first() / this.pageSize()) + 1 || 1,
        limit: this.pageSize(),
        search: this.globalFilter(),
        filters: event?.filters || {},
        sortBy: this.sortField(),
        sortOrder: this.sortOrder() > 0 ? 'asc' : 'desc',
        branchId: this.branchId() || undefined
      });
      const mappedData = res.data.map((m: any) => {
        const codes = m.movements?.map((x: any) => x.movement_code).filter(Boolean).sort() || [];
        const codeDisplay = codes.length > 0 
           ? codes.length === 1 
               ? `MOV-${String(codes[0]).padStart(5, '0')}` 
               : `MOV-${String(codes[0]).padStart(5, '0')} a MOV-${String(codes[codes.length-1]).padStart(5, '0')}`
           : '-';
        return {
          ...m,
          movement_code_display: codeDisplay
        };
      });
      this.data.set(mappedData);
      this.totalRecords.set(res.total);
      this.loading.set(false);
    } catch (error) {
      console.error(error);
      this.loading.set(false);
    }
  }

  newItem() {
    this.router.navigate(['/inventory/movements/new']);
  }

  editItem(item: any) {
    this.router.navigate(['/inventory/movements', item.id]);
  }

  async deleteItem(item: any) {
    this.confirmationService.confirm({
        message: '¿Está seguro de eliminar este movimiento? Esta acción revertirá los cambios de stock.',
        header: 'Confirmar Eliminación',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
            try {
                this.loading.set(true);
                await this.service.delete(this.type, item.id, item.v_);
                this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Movimiento eliminado correctamente' });
                this.loadData();
            } catch (error) {
                console.error(error);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el movimiento' });
            } finally {
                this.loading.set(false);
            }
        }
    });
  }
}
